import React, { useState } from "react";
import { X, MessageCircle, Send, Loader2, Sparkles, Bug, Lightbulb, Heart } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * Floating "Send feedback" pill — pinned bottom-right on desktop, hidden on
 * very narrow mobile so we don't overlap the "Made with Emergent" badge.
 * On mobile users can reach the same form via /feedback in the app menu.
 */
export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        data-testid="feedback-widget-open"
        aria-label="Send feedback"
        onClick={() => setOpen(true)}
        className={
          "hidden sm:inline-flex fixed bottom-5 right-5 z-40 items-center gap-2 " +
          "pl-3 pr-4 py-2.5 rounded-full text-white text-sm font-semibold " +
          "bg-brand-gradient shadow-[0_20px_50px_-15px_rgba(124,58,237,0.55)] " +
          "hover:opacity-95 active:scale-95 transition"
        }
      >
        <MessageCircle size={16} strokeWidth={2} />
        Send feedback
      </button>

      {open && (
        <FeedbackModal
          onClose={() => setOpen(false)}
          onSent={() => setOpen(false)}
        />
      )}
    </>
  );
}

const CATEGORIES = [
  { id: "praise", label: "Love it", icon: Heart, color: "#EC4899" },
  { id: "idea", label: "Idea", icon: Lightbulb, color: "#7C3AED" },
  { id: "bug", label: "Bug", icon: Bug, color: "#F97316" },
  { id: "general", label: "General", icon: Sparkles, color: "#6b7280" },
];

const CategoryPicker = ({ value, onChange }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500 font-semibold mb-2">
      Kind
    </div>
    <div className="flex flex-wrap gap-2">
      {CATEGORIES.map(({ id, label, icon: Icon, color }) => {
        const active = value === id;
        return (
          <button
            key={id}
            type="button"
            data-testid={`feedback-cat-${id}`}
            onClick={() => onChange(id)}
            className={
              "inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium border transition " +
              (active
                ? "bg-black text-white border-black"
                : "bg-white text-gray-700 border-[var(--border)] hover:border-black")
            }
          >
            <Icon size={13} strokeWidth={1.8} style={{ color: active ? "white" : color }} />
            {label}
          </button>
        );
      })}
    </div>
  </div>
);

const RatingPicker = ({ value, onChange }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500 font-semibold mb-2">
      Overall (optional)
    </div>
    <div className="flex items-center gap-1.5" data-testid="feedback-rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={`feedback-star-${n}`}
          onClick={() => onChange(value === n ? 0 : n)}
          aria-label={`${n} star${n === 1 ? "" : "s"}`}
          className={
            "w-9 h-9 rounded-lg flex items-center justify-center text-xl transition " +
            (value >= n
              ? "bg-[#FFF1E1] text-[#F97316]"
              : "text-gray-300 hover:text-[#F97316] hover:bg-[#FFF6EC]")
          }
        >
          ★
        </button>
      ))}
    </div>
  </div>
);

export function FeedbackModal({ onClose, onSent, embedded = false }) {
  const [category, setCategory] = useState("general");
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (message.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post("/feedback", {
        message: message.trim(),
        rating: rating || undefined,
        category,
        email: email.trim() || undefined,
        page: typeof window !== "undefined" ? window.location.pathname : undefined,
      });
      toast.success("Feedback sent — thank you.");
      setMessage("");
      setRating(0);
      onSent?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const inner = (
    <form
      onSubmit={submit}
      data-testid="feedback-form"
      className={
        "w-full max-w-md bg-white rounded-2xl border border-[var(--border)] shadow-[0_30px_80px_-25px_rgba(15,23,42,0.25)] " +
        "overflow-hidden"
      }
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-5 sm:px-6 py-4 sm:py-5 border-b border-[var(--border)] flex items-start justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#EC4899] font-semibold">
            Feedback
          </div>
          <div className="font-display text-xl sm:text-2xl tracking-tight font-semibold mt-1">
            What&apos;s on your mind?
          </div>
        </div>
        {!embedded && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close feedback"
            data-testid="feedback-close"
            className="-mr-1 -mt-1 p-2 text-gray-500 hover:text-black"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        )}
      </div>

      <div className="px-5 sm:px-6 py-5 space-y-5">
        <CategoryPicker value={category} onChange={setCategory} />
        <RatingPicker value={rating} onChange={setRating} />
        {/* Message */}
        <div>
          <label
            htmlFor="feedback-message"
            className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500 font-semibold block mb-2"
          >
            Tell us
          </label>
          <textarea
            id="feedback-message"
            data-testid="feedback-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            minLength={3}
            rows={5}
            placeholder="What worked? What broke? What would make Fount unmissable for you?"
            className="w-full border border-[var(--border)] focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 outline-none px-4 py-3 text-[15px] bg-white rounded-xl transition placeholder:text-gray-400 resize-y"
          />
        </div>

        {/* Email (optional for logged-out) */}
        <div>
          <label
            htmlFor="feedback-email"
            className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500 font-semibold block mb-2"
          >
            Email <span className="text-gray-400 normal-case tracking-normal font-mono">(so we can reply)</span>
          </label>
          <input
            id="feedback-email"
            type="email"
            data-testid="feedback-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@studio.com — optional"
            className="w-full border border-[var(--border)] focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/15 outline-none px-4 py-3 text-[15px] bg-white rounded-xl transition placeholder:text-gray-400"
          />
        </div>
      </div>

      <div className="px-5 sm:px-6 py-4 border-t border-[var(--border)] bg-[#faf7f1] flex items-center justify-between gap-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gray-500">
          Every note read
        </div>
        <button
          type="submit"
          disabled={busy || message.trim().length < 3}
          data-testid="feedback-submit"
          className="inline-flex items-center gap-2 bg-brand-gradient text-white px-5 py-2.5 rounded-xl font-semibold text-sm disabled:opacity-60 hover:opacity-95 transition-opacity shadow-[0_18px_50px_-15px_rgba(124,58,237,0.55)]"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} strokeWidth={2} />}
          {busy ? "Sending" : "Send"}
        </button>
      </div>
    </form>
  );

  if (embedded) return inner;

  return (
    <div
      data-testid="feedback-modal"
      onClick={onClose}
      className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-3 sm:p-6"
    >
      {inner}
    </div>
  );
}
